import {
  accessDenialFromClientApiError,
  derivePairedAccessIntakeReadiness,
  type AccessDenialReason,
  type CanonicalPresentationModelV1,
  type ClientJourneyPresentationV1,
} from "@enrein/shared-contracts";

export type ClientNextActionHomeInput = {
  readonly projectId?: string | null;
  readonly clientLinkStatus?: string | null;
  readonly intakeComplete: boolean;
  readonly previewReady: boolean;
  readonly accessErrorCode?: string | null;
  readonly openMissingInformationClarification?: boolean | null;
  readonly journeyPresentation?: ClientJourneyPresentationV1 | null;
};

export type ClientNextActionHomeResult = {
  readonly showPlatformMap: false;
  readonly nextActionLabel: string;
  readonly presentation: CanonicalPresentationModelV1 | null;
  readonly outcome: string | null;
};

export function resolveClientNextActionHome(input: ClientNextActionHomeInput): ClientNextActionHomeResult {
  const denial: AccessDenialReason | null = input.accessErrorCode
    ? accessDenialFromClientApiError(input.accessErrorCode)
    : null;

  const projectId = input.projectId?.trim() || "client_link_session";
  const paired = derivePairedAccessIntakeReadiness({
    projectId,
    accessDenial: denial,
    clientLink: input.clientLinkStatus
      ? { status: input.clientLinkStatus, accessReady: true }
      : denial
        ? { status: "EXPIRED" }
        : null,
    openMissingInformationClarification: input.openMissingInformationClarification ?? false,
    projectExists: true,
  });

  if (paired) {
    const label =
      paired.client.primaryAction?.label?.trim() || paired.client.title.trim() || "Continue";
    return {
      showPlatformMap: false,
      nextActionLabel: label,
      presentation: paired.client,
      outcome: paired.outcome,
    };
  }

  if (input.journeyPresentation?.presentation) {
    const presentation = input.journeyPresentation.presentation;
    const label = presentation.primaryAction?.label?.trim() || presentation.title.trim() || "Continue";
    return {
      showPlatformMap: false,
      nextActionLabel: label,
      presentation,
      outcome: input.journeyPresentation.outcome,
    };
  }

  if (input.clientLinkStatus === "INTAKE_SUBMITTED") {
    return {
      showPlatformMap: false,
      nextActionLabel: "Waiting for your agency",
      presentation: null,
      outcome: "intake_submitted",
    };
  }

  if (!input.intakeComplete) {
    return {
      showPlatformMap: false,
      nextActionLabel: "Complete your intake",
      presentation: null,
      outcome: null,
    };
  }
  if (!input.previewReady) {
    return {
      showPlatformMap: false,
      nextActionLabel: "Wait for preview",
      presentation: null,
      outcome: null,
    };
  }
  return {
    showPlatformMap: false,
    nextActionLabel: "Review and approve your site",
    presentation: null,
    outcome: null,
  };
}
